/**
 * CrewScreen — Social hub with 4 tabs: Crew, Faves, Search, Chat
 *
 * Replaces the EmptyScreen('crew') stub.
 * Follows the DiceScreen tab pattern with magenta accent.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { CrewTab } from '../components/crew/CrewTab';
import { FavesTab } from '../components/crew/FavesTab';
import { SearchTab } from '../components/crew/SearchTab';
import { ChatApp } from '../components/crew/ChatApp';
import { PlayerCard } from '../components/crew/PlayerCard';
import type { PlayerCardData } from '../components/crew/PlayerCard';
import type { FriendCellData } from '../components/crew/FriendCell';
import type { ChatService } from '../../services/ChatService';
import type { NetworkManager } from '../../network/NetworkManager';
import type { BlockService } from '../../services/BlockService';
import type { PendingFriendRequest } from '@sdl/shared';
import { showToast } from '../components/Toast';


// ─── Events ──────────────────────────────────────────────────────────

interface CrewScreenEvents {
  'action': { action: string; data?: any };
  /** A friend request was sent (drives the onboarding "add a friend" task). */
  'friend:request_sent': { playerId: string };
}

// ─── Component ───────────────────────────────────────────────────────

export class CrewScreen extends EventEmitter<CrewScreenEvents> {
  public element: HTMLElement;

  // Tabs
  private crewTab: CrewTab;
  private favesTab: FavesTab;
  private searchTab: SearchTab;
  private chatApp: ChatApp | null = null;

  // State
  private networkManager: NetworkManager | null = null;
  private blockService: BlockService | null = null;
  private chatService: ChatService | null = null;
  /** Connected-account check — daily gifts are locked for guest accounts. */
  private isLinked: (() => boolean) | null = null;
  private playerCard: PlayerCard | null = null;
  /** Removes the open card's friend:online/offline subscription. */
  private cardPresenceCleanup: (() => void) | null = null;
  /** Outgoing friend requests awaiting a response. Seeded from the server's
   *  friend:pending_list on auth (survives restarts), extended optimistically
   *  when a request is sent this session. Rendered as the Crew tab's pending
   *  section and consulted for the PENDING state on cards + search rows. */
  private pendingRequests: PendingFriendRequest[] = [];

  constructor() {
    super();

    // Create tab components
    this.crewTab = new CrewTab();
    this.favesTab = new FavesTab();
    this.searchTab = new SearchTab();

    // Build DOM
    this.element = this.createElement();
    this.injectStyles();
  }

  /**
   * Initialize with server data (called from auth:success handler)
   */
  initialize(options: {
    playerId: string;
    friends: FriendCellData[];
    favourites: string[];
    chatService: ChatService;
    networkManager: NetworkManager;
    blockService?: BlockService;
    /** Live connected-account check (guests can't send daily gifts). */
    isLinked?: () => boolean;
  }): void {
    this.networkManager = options.networkManager;
    this.blockService = options.blockService ?? null;
    this.isLinked = options.isLinked ?? null;
    this.chatService = options.chatService;

    // Mark favourites on friend data
    const friendsWithFaves = options.friends.map(f => ({
      ...f,
      isFavourite: options.favourites.includes(f.id),
    }));

    this.crewTab.setFriends(friendsWithFaves);
    this.favesTab.setFriends(friendsWithFaves);

    // Create chat app (needs chatService). resolvePeer lets the sidebar/header
    // pull the freshest avatar+name from the crew list instead of relying on
    // stored conversation metadata (which may predate a friend's avatar).
    this.chatApp = new ChatApp({
      chatService: options.chatService,
      localPlayerId: options.playerId,
      resolvePeer: (id) => this.getFriend(id),
    });
    // Tapping the peer in the chat header opens their player card.
    this.chatApp.on('profile:tap', ({ playerId }) => this.showPlayerCard(playerId));
    const chatPanel = this.element.querySelector('[data-panel="chat"]')!;
    chatPanel.innerHTML = '';
    chatPanel.appendChild(this.chatApp.element);

    // Wire network events
    this.setupNetworkListeners();
  }

  /**
   * Set friends list from enriched server data
   */
  setFriends(friends: FriendCellData[]): void {
    this.crewTab.setFriends(friends);
    this.favesTab.setFriends(friends);
  }

  updateOnlineStatus(playerId: string, online: boolean): void {
    this.crewTab.updateOnlineStatus(playerId, online);
    this.favesTab.updateOnlineStatus(playerId, online);
  }

  /** Friend record for a player, or undefined if not in the local crew.
   *  Source of truth for "is X my friend?" used by the body-level player card. */
  getFriend(playerId: string): FriendCellData | undefined {
    return this.crewTab.getFriends().find((f) => f.id === playerId);
  }

  /** True if we sent this player a friend request that's still unanswered.
   *  Source of truth for the PENDING state on the body-level player card. */
  isPendingRequest(playerId: string): boolean {
    return this.pendingRequests.some(p => p.playerId === playerId);
  }

  /** Record an outgoing request sent from any surface (search row, crew-modal
   *  card, lobby/in-game card) so every PENDING consumer stays in sync. */
  notePendingRequest(request: PendingFriendRequest): void {
    if (!this.isPendingRequest(request.playerId)) {
      this.pendingRequests.push(request);
      this.crewTab.setPendingRequests(this.pendingRequests);
    }
    this.searchTab.markAsPending(request.playerId);
  }

  /** Drop a pending entry (request answered or cancelled) from every surface. */
  private removePendingRequest(playerId: string): void {
    if (!this.isPendingRequest(playerId)) return;
    this.pendingRequests = this.pendingRequests.filter(p => p.playerId !== playerId);
    this.crewTab.setPendingRequests(this.pendingRequests);
    this.searchTab.setPendingIds(this.pendingRequests.map(p => p.playerId));
  }

  /** Refresh chat conversations (call when screen becomes visible) */
  onShow(): void {
    this.chatApp?.load();
  }

  /** Leaving the screen: drop chat partners that never got a message. */
  onHide(): void {
    void this.chatService?.clearEmptyDrafts();
  }

  /** Favourite friends (freshest crew-list data) for the floating chat's
   *  FAV CREW tab. */
  getFavouriteFriends(): FriendCellData[] {
    return this.crewTab.getFriends().filter(f => f.isFavourite);
  }

  /** Switch to chat tab and open a specific conversation */
  openChat(peerId: string, peerUsername?: string, peerPicture?: string): void {
    // Switch to chat tab
    const tabs = this.element.querySelectorAll<HTMLElement>('.crew-tab-btn');
    const panels = this.element.querySelectorAll<HTMLElement>('.crew-tab-panel');
    tabs.forEach(b => b.classList.toggle('active', b.dataset.tab === 'chat'));
    panels.forEach(p => p.classList.toggle('active', p.dataset.panel === 'chat'));
    this.chatApp?.openConversation(peerId, peerUsername, peerPicture);
  }

  destroy(): void {
    this.cardPresenceCleanup?.();
    this.cardPresenceCleanup = null;
    this.crewTab.destroy();
    this.favesTab.destroy();
    this.searchTab.destroy();
    this.chatApp?.destroy();
    this.playerCard?.destroy();
    this.element.remove();
    this.removeAllListeners();
  }

  // ─── DOM Creation ──────────────────────────────────────────────────

  private createElement(): HTMLElement {
    const root = document.createElement('div');

    root.innerHTML = `
      <div class="crew-screen-content">
        <!-- Screen header -->
        <div class="sdl-screen-header">
          <div class="sdl-screen-title magenta">MY CREW</div>
          <div class="sdl-screen-subtitle">FRIENDS &amp; RIVALS</div>
        </div>

        <!-- Tab strip -->
        <div class="crew-tab-strip">
          <button class="crew-tab-btn active" data-tab="crew">CREW</button>
          <button class="crew-tab-btn" data-tab="faves">FAVES</button>
          <button class="crew-tab-btn" data-tab="search">SEARCH</button>
          <button class="crew-tab-btn" data-tab="chat">CHAT</button>
        </div>

        <!-- Tab panels -->
        <div class="crew-tab-panel active" data-panel="crew"></div>
        <div class="crew-tab-panel" data-panel="faves"></div>
        <div class="crew-tab-panel" data-panel="search"></div>
        <div class="crew-tab-panel" data-panel="chat"></div>
      </div>

      <!-- Player card overlay slot -->
      <div class="crew-card-slot"></div>
    `;

    // Mount tab components into panels
    root.querySelector('[data-panel="crew"]')!.appendChild(this.crewTab.element);
    root.querySelector('[data-panel="faves"]')!.appendChild(this.favesTab.element);
    root.querySelector('[data-panel="search"]')!.appendChild(this.searchTab.element);

    // Tab switching
    this.setupTabListeners(root);

    // Wire tab component events
    this.setupTabEvents();

    return root;
  }

  private setupTabListeners(root: HTMLElement): void {
    const tabs = root.querySelectorAll<HTMLElement>('.crew-tab-btn');
    const panels = root.querySelectorAll<HTMLElement>('.crew-tab-panel');

    tabs.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tab;
        tabs.forEach(b => b.classList.toggle('active', b === btn));
        panels.forEach(p => p.classList.toggle('active', p.dataset.panel === target));

        // Load chat conversations when switching to chat tab
        if (target === 'chat' && this.chatApp) {
          this.chatApp.load();
        }
      });
    });
  }

  private setupTabEvents(): void {
    // Crew + Faves: player select -> show player card
    const handleSelect = ({ playerId }: { playerId: string }) => {
      this.showPlayerCard(playerId);
    };

    const handleFavourite = ({ playerId, isFavourite }: { playerId: string; isFavourite: boolean }) => {
      this.networkManager?.sendFriendFavourite(playerId, isFavourite);
      this.crewTab.updateFavourite(playerId, isFavourite);
      this.favesTab.updateFavourite(playerId, isFavourite);
    };

    // Daily gift: optimistic coin flyout + cooldown, server verdict reconciles
    // via friend_gift:sent (see setupNetworkListeners). Guests are stopped
    // BEFORE the coin flies (the server enforces this too).
    const handleGift = ({ playerId }: { playerId: string }) => {
      if (this.isLinked && !this.isLinked()) {
        showToast('Connect your account to send gifts. You can connect in Settings.', { type: 'info' });
        return;
      }
      this.networkManager?.sendFriendGift(playerId);
      this.crewTab.markGifted(playerId);
      this.favesTab.markGifted(playerId);
    };

    this.crewTab.on('player:select', handleSelect);
    this.crewTab.on('player:favourite', handleFavourite);
    this.crewTab.on('player:gift', handleGift);

    // Cancel an outgoing friend request from the Crew tab's pending section.
    // Optimistic removal; the server deletes the recipient's inbox entry.
    this.crewTab.on('pending:cancel', ({ playerId }) => {
      this.networkManager?.sendFriendRequestCancel(playerId);
      this.removePendingRequest(playerId);
    });

    this.favesTab.on('player:select', handleSelect);
    this.favesTab.on('player:favourite', handleFavourite);
    this.favesTab.on('player:gift', handleGift);

    // Search
    this.searchTab.on('search', ({ query }) => {
      this.networkManager?.searchPlayers(query);
    });
    this.searchTab.on('player:add', ({ playerId }) => {
      this.networkManager?.sendFriendAdd(playerId);
      const r = this.searchTab.getResult(playerId);
      this.notePendingRequest({
        playerId,
        username: r?.username ?? 'Player',
        picture: r?.picture ?? '',
        stacks: r?.stacks ?? 0,
      });
      this.emit('friend:request_sent', { playerId });
    });
    this.searchTab.on('player:unblock', ({ playerId }) => {
      this.networkManager?.sendFriendUnblock(playerId);
      this.blockService?.remove(playerId); // optimistic — server re-sends blocked:list
      this.searchTab.markAsUnblocked(playerId);
    });
    // A searched player isn't in the crew — pass the loaded result so the card
    // shows real name/picture/balance instead of placeholders.
    this.searchTab.on('player:select', ({ playerId }) => {
      const r = this.searchTab.getResult(playerId);
      this.showPlayerCard(
        playerId,
        r ? { username: r.username, picture: r.picture, stacks: r.stacks, crewTag: r.crewTag } : undefined,
      );
    });
  }

  private setupNetworkListeners(): void {
    if (!this.networkManager) return;

    // Enriched friend list (sent right after auth) — replaces the ID-only
    // placeholders with real names, pictures, and balances.
    this.networkManager.on('friend:list', (data) => {
      this.setFriends(data.friends as unknown as FriendCellData[]);
    });

    // Outgoing requests still awaiting a response (sent on auth) — restores
    // the PENDING state across restarts. Declined requests drop off the list.
    this.networkManager.on('friend:pending_list', (data) => {
      this.pendingRequests = data.requests;
      this.crewTab.setPendingRequests(this.pendingRequests);
      this.searchTab.setPendingIds(this.pendingRequests.map(p => p.playerId));
    });

    // Search results
    this.networkManager.on('player:search_results', (data) => {
      this.searchTab.setResults(data.results);
    });

    // Friend added
    this.networkManager.on('friend:added', (data) => {
      this.removePendingRequest(data.playerId);
      if (data.friend) {
        this.crewTab.addFriend(data.friend as unknown as FriendCellData);
        this.favesTab.setFriends(this.crewTab.getFriends());
        this.searchTab.markAsFriend(data.playerId);
      }
    });

    // Friend removed
    this.networkManager.on('friend:removed', (data) => {
      this.crewTab.removeFriend(data.playerId);
      this.favesTab.setFriends(this.crewTab.getFriends());
    });

    // Friend online/offline
    this.networkManager.on('friend:online', (data) => {
      this.updateOnlineStatus(data.playerId, true);
    });
    this.networkManager.on('friend:offline', (data) => {
      this.updateOnlineStatus(data.playerId, false);
    });

    // Favourite confirmed
    this.networkManager.on('friend:favourited', (data) => {
      this.crewTab.updateFavourite(data.playerId, data.isFavourite);
      this.favesTab.updateFavourite(data.playerId, data.isFavourite);
    });

    // Daily-gift verdict. On rejection re-arm the button; the optimistic coin
    // already flew, so a quiet toast explains why nothing landed.
    this.networkManager.on('friend_gift:sent', (data) => {
      if (data.ok) return;
      if (data.reason === 'already_gifted') {
        // Cooldown is correct — keep the button dim, just explain.
        showToast('Already gifted today. Come back tomorrow.', { type: 'info' });
        return;
      }
      // Anything else: re-arm so the player can retry.
      this.crewTab.setGiftAvailable(data.playerId, true);
      this.favesTab.setGiftAvailable(data.playerId, true);
      if (data.reason === 'guest') {
        // Server backstop for the client-side guest gate.
        showToast('Connect your account to send gifts. You can connect in Settings.', { type: 'info' });
      } else {
        showToast('Could not send the gift. Please try again.', { type: 'error' });
      }
    });
  }

  /**
   * Open the player card overlay. `fallback` carries display data for a player
   * who isn't in the crew (e.g. a search result) so the card populates with
   * real name/picture/balance instead of placeholders.
   */
  private showPlayerCard(
    playerId: string,
    fallback?: { username?: string; picture?: string; stacks?: number; crewTag?: string; isOnline?: boolean },
  ): void {
    // Find friend data — the crew record is the freshest, fall back otherwise.
    // A pending-request entry also carries display data (pending row taps
    // pass no explicit fallback).
    const friends = this.crewTab.getFriends();
    const friend = friends.find(f => f.id === playerId);
    const pending = this.pendingRequests.find(p => p.playerId === playerId);

    const cardData: PlayerCardData = {
      id: playerId,
      username: friend?.username ?? fallback?.username ?? pending?.username ?? 'Player',
      picture: friend?.picture ?? fallback?.picture ?? pending?.picture ?? '',
      stacks: friend?.stacks ?? fallback?.stacks ?? pending?.stacks ?? 0,
      crewTag: friend?.crewTag ?? fallback?.crewTag,
      isOnline: friend?.isOnline ?? fallback?.isOnline ?? false,
      isFriend: !!friend,
      isBlocked: this.blockService?.has(playerId) ?? false,
      isPending: !friend && this.isPendingRequest(playerId),
    };

    // Destroy previous card
    this.playerCard?.destroy();

    this.playerCard = new PlayerCard(cardData);
    const slot = this.element.querySelector('.crew-card-slot')!;
    slot.innerHTML = '';
    slot.appendChild(this.playerCard.element);
    this.playerCard.show();

    // Live presence: keep the dot in sync while the card is open. The server
    // only pushes friend:online/offline for friends, so this only applies to
    // crew members — strangers never get a (stale) dot.
    this.cardPresenceCleanup?.();
    this.cardPresenceCleanup = null;
    if (this.networkManager && cardData.isFriend) {
      const nm = this.networkManager;
      const onOnline = (d: { playerId: string }) => {
        if (d.playerId === playerId) this.playerCard?.setOnline(true);
      };
      const onOffline = (d: { playerId: string }) => {
        if (d.playerId === playerId) this.playerCard?.setOnline(false);
      };
      nm.on('friend:online', onOnline);
      nm.on('friend:offline', onOffline);
      this.cardPresenceCleanup = () => {
        nm.off('friend:online', onOnline);
        nm.off('friend:offline', onOffline);
      };
    }

    // Wire card actions
    this.playerCard.on('action:chat', ({ playerId }) => {
      this.playerCard?.hide();
      this.openChat(playerId, cardData.username, cardData.picture);
    });

    this.playerCard.on('action:remove', ({ playerId }) => {
      this.networkManager?.sendFriendRemove(playerId);
      this.playerCard?.hide();
    });

    this.playerCard.on('action:block', ({ playerId }) => {
      this.networkManager?.sendFriendBlock(playerId);
      // Optimistic — hide them from the lobby/chat immediately; the server's
      // blocked:list reconciles a moment later.
      this.blockService?.add({ id: playerId, username: cardData.username, picture: cardData.picture });
      this.playerCard?.hide();
      this.crewTab.removeFriend(playerId);
      this.favesTab.setFriends(this.crewTab.getFriends());
    });

    this.playerCard.on('action:add', ({ playerId }) => {
      this.networkManager?.sendFriendAdd(playerId);
      this.notePendingRequest({
        playerId,
        username: cardData.username,
        picture: cardData.picture,
        stacks: cardData.stacks ?? 0,
      });
      this.emit('friend:request_sent', { playerId });
    });

    this.playerCard.on('action:unblock', ({ playerId }) => {
      this.networkManager?.sendFriendUnblock(playerId);
      this.blockService?.remove(playerId); // optimistic — server re-sends blocked:list
      this.playerCard?.hide();
      this.cardPresenceCleanup?.();
      this.cardPresenceCleanup = null;
    });

    this.playerCard.on('action:close', () => {
      this.playerCard?.hide();
      this.cardPresenceCleanup?.();
      this.cardPresenceCleanup = null;
    });
  }

  // ─── Styles ────────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById('crew-screen-styles')) return;
    const style = document.createElement('style');
    style.id = 'crew-screen-styles';
    style.textContent = CREW_STYLES;
    document.head.appendChild(style);
  }
}

// ─── CSS ─────────────────────────────────────────────────────────────

const CREW_STYLES = `
/* ─── Crew Screen Layout ─── */
.crew-screen-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* ─── Tab Strip ─── */
.crew-tab-strip {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  padding: 0 16px;
  flex-shrink: 0;
}

.crew-tab-btn {
  flex: 1;
  padding: 12px 0 10px;
  background: none;
  border: none;
  color: rgba(255,255,255,0.4);
  font-family: 'Bebas Neue', sans-serif;
  font-size: 16px;
  letter-spacing: 1px;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.crew-tab-btn.active {
  color: var(--magenta, #FF0080);
}

.crew-tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  right: 20%;
  height: 2px;
  background: var(--magenta, #FF0080);
  box-shadow: 0 0 8px var(--magenta, #FF0080);
  border-radius: 1px;
}

/* ─── Tab Panels ─── */
.crew-tab-panel {
  display: none;
  flex: 1;
  overflow: hidden;
}

.crew-tab-panel.active {
  display: flex;
  flex-direction: column;
}

/* ─── Tab Content ─── */
.crew-tab-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

/* ─── Friend List ─── */
.crew-friend-list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  padding: 8px 12px;
  gap: 2px;
}

/* ─── Pending Requests (outgoing, above the friends list) ─── */
.crew-pending-section {
  display: flex;
  flex-direction: column;
  padding: 8px 12px 0;
  gap: 2px;
  flex-shrink: 0;
  /* Breathing room before the friends list. The section is display:none
     while there are no pending requests, so this margin only exists when
     the section does — an empty inbox costs the list nothing. */
  margin-bottom: 12px;
}

.crew-pending-header {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 12px;
  letter-spacing: 1.5px;
  color: rgba(255, 215, 0, 0.55);
  padding: 4px 12px 2px;
}

.crew-pending-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}

.crew-pending-cell:active {
  background: rgba(255,255,255,0.04);
}

.crew-pending-cell .crew-cell-name {
  color: rgba(255,255,255,0.65);
}

.crew-pending-tag {
  color: rgba(255, 215, 0, 0.7);
  border: 1px solid rgba(255, 215, 0, 0.25);
  background: rgba(255, 215, 0, 0.08);
  border-radius: 16px;
  padding: 4px 10px;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 12px;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.crew-pending-cancel {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.crew-pending-cancel:active {
  background: rgba(255, 60, 60, 0.18);
  color: #FF5C5C;
}

/* ─── Friend Cell ─── */
.crew-friend-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}

.crew-friend-cell:active {
  background: rgba(255,255,255,0.04);
}

.crew-cell-avatar {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--concrete-lighter, #242429);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.crew-cell-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  overflow: hidden;
}

.crew-cell-avatar-initial {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 18px;
  color: rgba(255,255,255,0.6);
}

.crew-cell-online-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  background: var(--green, #00FF88);
  border-radius: 50%;
  border: 2px solid var(--dark-bg, #08080A);
}

.crew-cell-info {
  flex: 1;
  min-width: 0;
}

.crew-cell-name {
  font-family: 'Rajdhani', sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: rgba(255,255,255,0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.crew-cell-meta {
  font-family: 'Rajdhani', sans-serif;
  font-size: 12px;
  color: rgba(255,255,255,0.35);
}

.crew-cell-stacks {
  color: #00FF88;
}

.crew-cell-heart,
.crew-cell-gift {
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.15s, transform 0.15s;
  flex-shrink: 0;
}

.crew-cell-heart:active,
.crew-cell-gift:active {
  transform: scale(1.2);
}

.crew-cell-heart.active {
  opacity: 1;
}

/* Daily gift sent — dim until tomorrow. */
.crew-cell-gift.sent {
  opacity: 0.35;
  cursor: default;
}
.crew-cell-gift.sent:active {
  transform: none;
}

/* ─── Empty States ─── */
.crew-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
  text-align: center;
}

.crew-empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.crew-empty-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 20px;
  color: rgba(255,255,255,0.5);
  letter-spacing: 2px;
  margin-bottom: 8px;
}

.crew-empty-text {
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  color: rgba(255,255,255,0.3);
  max-width: 260px;
}

/* ─── Search ─── */
.crew-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin: 12px 12px 8px;
  background: var(--glass, rgba(255,255,255,0.03));
  border: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  border-radius: 12px;
  transition: border-color 0.2s;
}

.crew-search-bar:focus-within {
  border-color: var(--magenta, #FF0080);
  box-shadow: 0 0 12px rgba(255,0,128,0.15);
}

.crew-search-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: rgba(255,255,255,0.9);
  font-family: 'Rajdhani', sans-serif;
  font-size: 15px;
}

.crew-search-input::placeholder {
  color: rgba(255,255,255,0.25);
}

.crew-search-results {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  padding: 0 12px;
  gap: 2px;
}

.crew-search-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
}

.crew-search-row:active {
  background: rgba(255,255,255,0.04);
}

.crew-search-add-btn {
  background: var(--magenta, #FF0080);
  color: white;
  border: none;
  border-radius: 16px;
  padding: 6px 14px;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.crew-search-add-btn:active {
  opacity: 0.7;
}

.crew-search-added {
  flex-shrink: 0;
  padding: 6px;
}

.crew-search-pending {
  color: rgba(255, 215, 0, 0.7);
  border: 1px solid rgba(255, 215, 0, 0.25);
  background: rgba(255, 215, 0, 0.08);
  border-radius: 16px;
  padding: 6px 14px;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 13px;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.crew-search-unblock-btn {
  color: var(--green, #00FF88);
  border: 1px solid rgba(0, 255, 136, 0.4);
  background: rgba(0, 255, 136, 0.1);
  border-radius: 16px;
  padding: 6px 14px;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.crew-search-unblock-btn:active {
  opacity: 0.7;
}

/* ─── Player Card ─── */
.crew-card-slot {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
}

.crew-player-card {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: none;
  pointer-events: auto;
}

.crew-player-card.visible {
  display: flex;
}

.crew-card-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.crew-card-panel {
  position: relative;
  margin: auto;
  width: 280px;
  background: var(--concrete, #111114);
  border: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  border-radius: 20px;
  padding: 32px 24px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.crew-card-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  opacity: 0.6;
}

.crew-card-avatar-wrap {
  width: 80px;
  height: 80px;
  position: relative;
}

/* Inner element owns the circular clip + ring so the online dot (a sibling)
   can sit at the edge without being cropped by overflow:hidden. */
.crew-card-avatar-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--concrete-lighter, #242429);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid rgba(255,255,255,0.1);
}

.crew-card-avatar-wrap.online .crew-card-avatar-inner {
  border-color: var(--green, #00FF88);
  box-shadow: 0 0 12px rgba(0,255,136,0.3);
}

.crew-card-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: zoom-in;
}

.crew-card-avatar-initial {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 32px;
  color: rgba(255,255,255,0.5);
}

/* Presence dot — matches the lobby "ON THE BLOCK" badge dot. */
.crew-card-online-dot {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--green, #00FF88);
  border: 3px solid var(--concrete, #111114);
  box-shadow: 0 0 6px var(--green, #00FF88);
}

.crew-card-username {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 22px;
  color: white;
  letter-spacing: 1px;
  margin-top: 4px;
}

.crew-card-meta {
  font-family: 'Rajdhani', sans-serif;
  font-size: 13px;
  color: rgba(255,255,255,0.4);
  margin-bottom: 12px;
}

.crew-card-actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

.crew-card-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 8px;
  border-radius: 12px;
  border: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  background: var(--glass, rgba(255,255,255,0.03));
  color: rgba(255,255,255,0.7);
  font-family: 'Bebas Neue', sans-serif;
  font-size: 11px;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.crew-card-btn:active {
  background: rgba(255,255,255,0.06);
}

.crew-card-btn-chat {
  border-color: rgba(255,0,128,0.3);
  color: var(--magenta, #FF0080);
}

.crew-card-btn-remove,
.crew-card-btn-block {
  border-color: rgba(255,51,102,0.2);
  color: rgba(255,51,102,0.7);
}

.crew-card-btn-remove.confirming,
.crew-card-btn-block.confirming {
  background: rgba(255,51,102,0.15);
  border-color: var(--red, #FF3366);
  color: var(--red, #FF3366);
}

.crew-card-btn-add,
.crew-card-btn-unblock {
  border-color: rgba(0,255,136,0.3);
  color: var(--green, #00FF88);
}

.crew-card-btn-pending {
  border-color: rgba(255, 215, 0, 0.25);
  color: rgba(255, 215, 0, 0.6);
  opacity: 0.7;
  cursor: default;
}

/* Chat locked: not a friend (or blocked). Friends-only messaging. */
.crew-card-btn-disabled {
  border-color: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.3);
  opacity: 0.55;
  cursor: not-allowed;
}
.crew-card-btn-disabled:active {
  background: var(--glass, rgba(255,255,255,0.03));
}

.crew-card-chat-hint {
  width: 100%;
  margin-top: 10px;
  text-align: center;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: rgba(255,255,255,0.4);
}

/* ─── Chat App ─── */
.chat-app {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.chat-sidebar {
  width: 56px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0;
  gap: 8px;
  overflow-y: auto;
  border-right: 1px solid var(--glass-border, rgba(255,255,255,0.06));
}

.chat-sidebar-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--concrete-lighter, #242429);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;
  flex-shrink: 0;
  position: relative;
}

.chat-sidebar-avatar.active {
  border-color: var(--magenta, #FF0080);
  box-shadow: 0 0 8px rgba(255,0,128,0.3);
}

.chat-sidebar-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  overflow: hidden;
}

.chat-sidebar-avatar span {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 16px;
  color: rgba(255,255,255,0.5);
}

.chat-sidebar-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 16px;
  height: 16px;
  background: var(--red, #FF3366);
  border-radius: 8px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* ─── Chat Panel ─── */
.chat-panel-container {
  flex: 1;
  overflow: hidden;
}

.chat-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  flex-shrink: 0;
}

.chat-header-id {
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
}
.chat-header-id:active {
  background: rgba(255,255,255,0.06);
}

.chat-header-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,0,128,0.15);
  color: var(--magenta, #FF0080);
  font-family: 'Bebas Neue', sans-serif;
  font-size: 16px;
  flex-shrink: 0;
}
.chat-header-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chat-header-name {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 18px;
  color: rgba(255,255,255,0.9);
  letter-spacing: 1px;
}

.chat-header-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  opacity: 0.5;
  transition: opacity 0.15s;
}

.chat-header-clear:hover {
  opacity: 0.8;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chat-bubble {
  max-width: 75%;
  padding: 8px 12px;
  border-radius: 14px;
  word-wrap: break-word;
}

.chat-bubble-self {
  align-self: flex-end;
  background: rgba(255,0,128,0.15);
  border: 1px solid rgba(255,0,128,0.2);
  border-bottom-right-radius: 4px;
}

.chat-bubble-peer {
  align-self: flex-start;
  background: var(--glass, rgba(255,255,255,0.03));
  border: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  border-bottom-left-radius: 4px;
}

.chat-bubble-text {
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  color: rgba(255,255,255,0.85);
  line-height: 1.4;
}

.chat-bubble-time {
  font-family: 'Rajdhani', sans-serif;
  font-size: 10px;
  color: rgba(255,255,255,0.25);
  display: block;
  text-align: right;
  margin-top: 2px;
}

.chat-bubble-image img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
}

/* Photo blob evicted to reclaim space (and CDN copy is gone). */
.chat-bubble-image-expired {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px dashed rgba(255,255,255,0.15);
  border-radius: 8px;
  font-size: 13px;
  color: rgba(255,255,255,0.4);
}

.chat-bubble-invite {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255,215,0,0.08);
  border: 1px solid rgba(255,215,0,0.2);
  border-radius: 10px;
}

.chat-invite-icon {
  font-size: 20px;
}

.chat-invite-text {
  font-family: 'Rajdhani', sans-serif;
  font-size: 13px;
  color: var(--gold, #FFD700);
}

/* ─── Chat Input Bar ─── */
.chat-input-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  flex-shrink: 0;
}

.chat-input-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.chat-input-icon:active {
  opacity: 0.5;
}

.chat-text-input {
  flex: 1;
  /* Allow the input to shrink below its intrinsic placeholder width so the send
     button always fits inside the bar on narrow devices. Default min-width:auto
     on flex items would push the button off the right edge (POCO F3 etc). */
  min-width: 0;
  box-sizing: border-box;
  background: var(--glass, rgba(255,255,255,0.03));
  border: 1px solid var(--glass-border, rgba(255,255,255,0.06));
  border-radius: 20px;
  padding: 8px 14px;
  color: rgba(255,255,255,0.9);
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.chat-text-input:focus {
  border-color: rgba(255,0,128,0.3);
}

.chat-text-input::placeholder {
  color: rgba(255,255,255,0.2);
}

.chat-send-btn {
  background: var(--magenta, #FF0080);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.chat-send-btn:active {
  opacity: 0.7;
}

.chat-send-btn svg {
  color: white;
}

.chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
}
`;
